import { useContext, useEffect, useRef, useState } from "react";
import {
	Modal,
	Form,
	Radio,
	Space,
	Button,
	notification,
	Upload,
	Row,
	Col,
	Divider,
	Flex,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";

import { POST } from "../../../../providers/useAxiosQuery";
import { userData } from "../../../../providers/companyInfo";
import {
	checkNetworkStatus,
	checkPermissions,
	readFile,
	setFileStorage,
} from "../../../../providers/useCapacitorStorage";
import imageResizer from "../../../../providers/imageResizer";
import notificationErrors from "../../../../providers/notificationErrors";
import validateRules from "../../../../providers/validateRules";
import FloatTextArea from "../../../../providers/FloatTextArea";
import PageFacultyMonitoringContext from "./PageFacultyMonitoringContext";

export default function ModalFormUpdateStatus(props) {
	const {
		toggleModalUpdateStatus,
		setToggleModalUpdateStatus,
		dataStatus,
		setDataFacultyLoadSchedule,
		setDataFacultyLoadScheduleStatus,
	} = useContext(PageFacultyMonitoringContext);
	const [form] = Form.useForm();

	const [fileList, setFileList] = useState([]);
	const [openCamera, setOpenCamera] = useState(false);
	const [cameraReady, setCameraReady] = useState(false);
	const videoRef = useRef(null);
	const streamRef = useRef(null);

	const { mutate: mutateUpdateStatus, isLoading: isLoadingUpdateStatus } = POST(
		`api/faculty_load_status`,
		"faculty_load_schedule_list",
	);

	const handleChangeUpload = async ({ fileList: newFileList }) => {
		try {
			const modifiedFiles = await Promise.all(
				newFileList.map(async (item) => {
					if (item.base64 && item.originFileObj) {
						return item;
					}

					const compressedImage = await imageResizer(item.originFileObj);
					const compressedImagebase64 = await imageResizer(
						item.originFileObj,
						"base64",
					);

					return {
						originFileObj: compressedImage,
						is_delete: 0,
						lastModified: compressedImage.lastModified,
						lastModifiedDate: compressedImage.lastModifiedDate,
						name: compressedImage.name,
						size: compressedImage.size,
						type: compressedImage.type,
						uid: item.uid,
						status: "done",
						base64: compressedImagebase64,
					};
				}),
			);

			setFileList(modifiedFiles);
		} catch (error) {
			console.error("Error occurred:", error);
		}
	};

	const stopCamera = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		setCameraReady(false);
	};

	const startCamera = async () => {
		try {
			stopCamera();
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: "environment" } },
				audio: false,
			});

			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();
				setCameraReady(true);
			}
		} catch (error) {
			setOpenCamera(false);
			notification.error({
				description:
					"Unable to open camera. You can still upload a file instead.",
			});
		}
	};

	const capturePhoto = async () => {
		if (!videoRef.current || !cameraReady) return;

		const video = videoRef.current;
		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth || 1280;
		canvas.height = video.videoHeight || 720;

		const context = canvas.getContext("2d");
		if (!context) return;

		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		try {
			const base64Raw = canvas.toDataURL("image/jpeg", 0.9);
			const blob = await (await fetch(base64Raw)).blob();
			const filename = `camera_${dayjs().format("YYYYMMDD_HHmmss")}.jpg`;
			const capturedFile = new File([blob], filename, {
				type: "image/jpeg",
				lastModified: Date.now(),
			});

			const compressedImage = await imageResizer(capturedFile);
			const compressedImagebase64 = await imageResizer(capturedFile, "base64");

			setFileList((prev) => [
				...prev,
				{
					originFileObj: compressedImage,
					is_delete: 0,
					lastModified: compressedImage.lastModified,
					lastModifiedDate: compressedImage.lastModifiedDate,
					name: compressedImage.name,
					size: compressedImage.size,
					type: compressedImage.type,
					uid: `${Date.now()}`,
					status: "done",
					base64: compressedImagebase64,
				},
			]);

			notification.success({
				description: "Photo captured.",
			});
		} catch (error) {
			notification.error({
				description: "Failed to capture photo. Please try again.",
			});
		}
	};

	const onFinish = (values) => {
		checkNetworkStatus().then((res) => {
			if (res.connected) {
				let data = new FormData();

				data.append(
					"faculty_load_schedule_id",
					toggleModalUpdateStatus.data.id,
				);
				data.append(
					"faculty_load_id",
					toggleModalUpdateStatus.data.faculty_load_id,
				);

				data.append("created_at", dayjs().format("YYYY-MM-DD HH:mm:ss"));
				data.append("updated_at", dayjs().format("YYYY-MM-DD HH:mm:ss"));
				data.append("created_by", userData().id);

				data.append("status_id", values.status_id);
				data.append("remarks", values.remarks);
				data.append(
					"link_origin",
					process.env.REACT_APP_FACULTY_MONITORING_URL,
				);

				let fileCounter = 0;
				if (fileList.length > 0) {
					for (let x = 0; x < fileList.length; x++) {
						const elem = fileList[x];

						if (elem.originFileObj) {
							fileCounter++;
							data.append(`file_${x}`, elem.originFileObj, elem.name);
						}
					}
				}
				data.append("fileCounter", fileCounter);

				mutateUpdateStatus(data, {
					onSuccess: (res) => {
						// console.log("mutateFormUpload res", res);
						if (res.success) {
							notification.success({
								message: "Faculty Monitoring",
								description: res.message,
							});

							setToggleModalUpdateStatus({ open: false, data: null });
							setFileList([]);

							form.resetFields();
						} else {
							notification.error({
								message: "Faculty Monitoring",
								description: res.message,
							});
						}
					},
					onError: (err) => {
						// notification.error({
						// 	message: "Faculty Monitoring",
						// 	description: "Something Went Wrong",
						// });
						notificationErrors(err);
					},
				});
			} else {
				checkPermissions().then((res) => {
					if (res && res.publicStorage === "granted") {
						let data = {
							...values,
							faculty_load_schedule_id: toggleModalUpdateStatus.data.id,
							faculty_load_id: toggleModalUpdateStatus.data.faculty_load_id,
							fullname: toggleModalUpdateStatus.data.fullname,
							building: toggleModalUpdateStatus.data.building,
							code: toggleModalUpdateStatus.data.code,
							date_sync: toggleModalUpdateStatus.data.date_sync,
							dayschedule: toggleModalUpdateStatus.data.dayschedule,
							deparment: toggleModalUpdateStatus.data.deparment,
							floor: toggleModalUpdateStatus.data.floor,
							room_code: toggleModalUpdateStatus.data.room_code,
							meridian: toggleModalUpdateStatus.data.meridian,
							school_year: toggleModalUpdateStatus.data.school_year,
							section: toggleModalUpdateStatus.data.section,
							semester: toggleModalUpdateStatus.data.semester,
							time_in: toggleModalUpdateStatus.data.time_in,
							time_out: toggleModalUpdateStatus.data.time_out,
							created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
							updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
							created_by: userData().id,
							fileList: JSON.stringify(fileList),
						};

						setFileStorage({
							foldername: "faculty_load_attendance",
							filename: "faculty_load_schedule_status",
							data,
							override: true,
						});
						setDataFacultyLoadScheduleStatus((ps) => [...ps, data]);

						readFile(
							"faculty_load_attendance/faculty_load_schedule_list.txt",
						).then((data) => {
							if (data) {
								let filterDataSourceFacultyLoad = data.filter(
									(f) =>
										f.id !== toggleModalUpdateStatus.data.id &&
										dayjs().format("YYYY-MM-DD") ===
											dayjs(f.data_sync).format("YYYY-MM-DD"),
								);

								setDataFacultyLoadSchedule(filterDataSourceFacultyLoad);

								setFileStorage({
									foldername: "faculty_load_attendance",
									filename: "faculty_load_schedule_list",
									filterDataSourceFacultyLoad,
								});
							}
						});

						notification.success({
							message: "Faculty Monitoring",
							description: "Data save in local",
						});

						setToggleModalUpdateStatus({ open: false, data: null });

						form.resetFields();
						setFileList([]);
					}
				});
			}
		});
	};

	const handleRenderStatus = () => {
		if (dataStatus) {
			return dataStatus.map((item, index) => {
				return (
					<Radio value={item.id} key={index}>
						{item.status}
					</Radio>
				);
			});
		} else {
			return null;
		}
	};

	useEffect(() => {
		if (toggleModalUpdateStatus && toggleModalUpdateStatus.open) {
			form.resetFields();
			setOpenCamera(false);
		}

		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [toggleModalUpdateStatus]);

	useEffect(() => {
		if (openCamera) {
			startCamera();
		} else {
			stopCamera();
		}

		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openCamera]);

	useEffect(() => {
		return () => {
			stopCamera();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Modal
			wrapClassName="modal-wrap-form-update-status"
			title={
				<>
					<b>
						{toggleModalUpdateStatus &&
							toggleModalUpdateStatus.data &&
							toggleModalUpdateStatus.data.fullname}
					</b>
				</>
			}
			open={toggleModalUpdateStatus.open}
			onCancel={() => {
				setToggleModalUpdateStatus({ open: false, data: null });
				setOpenCamera(false);
				stopCamera();
			}}
			footer={[
				<Button
					className="btn-main-primary outlined"
					onClick={() => {
						setToggleModalUpdateStatus({ open: false, data: null });
						setOpenCamera(false);
						stopCamera();
					}}
					key={1}
					disabled={isLoadingUpdateStatus}
				>
					CANCEL
				</Button>,
				<Button
					type="primary"
					className="btn-main-primary"
					onClick={() => {
						form.submit();
					}}
					key={2}
					loading={isLoadingUpdateStatus}
				>
					SUBMIT
				</Button>,
			]}
			forceRender
		>
			<Row gutter={[20, 20]}>
				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					{!openCamera ? (
						<Upload.Dragger
							className="upload-w-100 upload-hide-remove-icon"
							accept="image/png,image/jpg,image/jpeg"
							multiple
							beforeUpload={(file) => {
								let error = false;
								const isLt2M = file.size / 1024 / 1024 < 5;
								if (!isLt2M) {
									// message.error("Audio must smaller than 5MB!");
									notification.error({
										// message: "Faculty Monitoring Justification",
										description: "Image must be smaller than 5MB!",
									});
									error = Upload.LIST_IGNORE;
								}
								return error;
							}}
							onChange={handleChangeUpload}
							fileList={fileList}
						>
							<p className="ant-upload-drag-icon">
								<FontAwesomeIcon icon={faFileArrowUp} className="m-r-xs" />
							</p>
							<p className="ant-upload-text">
								Click or drag file to this area to upload
							</p>
							<p className="ant-upload-hint">
								Support for a single or bulk upload
							</p>
						</Upload.Dragger>
					) : (
						<div>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								style={{ width: "100%", borderRadius: 8, background: "#000" }}
							/>
							<Flex className="m-t-sm" justify="center" gap={10}>
								<Button
									className="btn-main-primary"
									onClick={capturePhoto}
									disabled={!cameraReady}
								>
									Capture Photo
								</Button>
							</Flex>
							<div className="m-t-sm">
								{cameraReady
									? "Camera ready. Capture photo, or switch to upload file instead."
									: "Starting camera..."}
							</div>
						</div>
					)}
				</Col>

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Flex justify="center" align="center">
						<Button
							className="btn-main-primary"
							onClick={() => {
								setOpenCamera((prev) => !prev);
							}}
						>
							{!openCamera ? "Open Camera" : "Upload File Instead"}
						</Button>
					</Flex>
				</Col>

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Divider orientation="left" plain />
				</Col>

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Form form={form} onFinish={onFinish}>
						<Form.Item name="status_id" rules={[validateRules.required]}>
							<Radio.Group>
								<Space direction="vertical">{handleRenderStatus()}</Space>
							</Radio.Group>
						</Form.Item>

						<Form.Item name="remarks" initialValue={"NA"}>
							<FloatTextArea placeholder="Remarks" label="Remarks" />
						</Form.Item>
					</Form>
				</Col>
			</Row>
		</Modal>
	);
}
